/**
 * Styles for FieldFork. Structural values that intentionally bypass tokens:
 * 1px borders, 40px hit-area minimum, 0.01ms reduced-motion sentinel,
 * zIndex 2000 (see portal/AGENTS.md). Width/maxHeight come from FieldFork.tsx
 * so positioning math and CSS stay in sync.
 */

import { makeStyles, tokens } from '@fluentui/react-components';
import { thinScrollbarStyles } from '@/styles/scrollbar';

export interface FieldForkStyleConstants {
  /** Width of the popover surface in pixels. */
  width: number;
  /** Maximum height of the popover surface in pixels. */
  maxHeight: number;
}

/**
 * Build the FieldFork styles. Width and maxHeight are passed in as
 * pixel literals so the component can drive them from the same
 * constants the JS positioning math uses (avoids drift between the
 * style and the `resolvePosition` helper in `FieldFork.tsx`).
 */
export const buildUseFieldForkStyles = ({
  width,
  maxHeight,
}: FieldForkStyleConstants) =>
  makeStyles({
    popover: {
      position: 'fixed',
      width: `${width}px`,
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: `${maxHeight}px`,
      display: 'flex',
      flexDirection: 'column',
      // Match the input rail (WorkflowInputPanel) so the popover reads
      // as the same surface the user already associates with token /
      // function browsing. `Background1` would have been correct in
      // isolation, but sits at the same lightness as the parameter
      // form behind the popover and made the popover look transparent.
      // Plus a 1px stroke for a crisp edge regardless of the underlying
      // content's brightness, and shadow28 for stronger elevation than
      // the previous shadow16 — the popover floats over a dense form,
      // so it needs to sit clearly above it.
      backgroundColor: tokens.colorNeutralBackground2,
      borderTopLeftRadius: tokens.borderRadiusLarge,
      borderTopRightRadius: tokens.borderRadiusLarge,
      borderBottomLeftRadius: tokens.borderRadiusLarge,
      borderBottomRightRadius: tokens.borderRadiusLarge,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      boxShadow: tokens.shadow28,
      // Above all editor surfaces (z-index is stacking context, not a
      // design-token concern — see AGENTS.md).
      zIndex: 2000,
      opacity: 0,
      transform: 'scale(0.96)',
      transformOrigin: 'top right',
      transitionProperty: 'opacity, transform',
      transitionDuration: tokens.durationNormal,
      transitionTimingFunction: tokens.curveDecelerateMid,
      overflow: 'hidden',
      '@media (prefers-reduced-motion: reduce)': {
        transitionProperty: 'opacity',
        transitionDuration: '0.01ms',
        transform: 'none',
      },
      '@media (forced-colors: active)': {
        border: '1px solid CanvasText',
        backgroundColor: 'Canvas',
      },
    },
    popoverAbove: {
      transformOrigin: 'bottom right',
    },
    popoverOpen: {
      opacity: 1,
      transform: 'scale(1)',
      '@media (prefers-reduced-motion: reduce)': {
        transform: 'none',
      },
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      columnGap: tokens.spacingHorizontalS,
      flexShrink: 0,
    },
    tabsWrapper: {
      flex: 1,
      minWidth: 0,
    },
    // 40x40 minimum click target for the dismiss button — WCAG 2.1
    // SC 2.5.5 (target size). The exact pixel literal is the
    // accessibility minimum, not a spacing concern.
    closeButton: {
      minWidth: '40px',
      minHeight: '40px',
      flexShrink: 0,
      marginRight: tokens.spacingHorizontalXS,
      marginTop: tokens.spacingVerticalXXS,
    },
    body: {
      flex: 1,
      minHeight: 0,
      overflow: 'auto',
      ...thinScrollbarStyles,
    },
  });
